/**
 * `Tag` — the riddim / label / style / year page. `docs/design-direction.md` §8.6
 * (last sentence): "Tag views use the identical layout with a one-line definition in
 * place of the biography." `spec.md` §6.4, `GET /api/tags/:id`.
 *
 * Every visual decision — the `SpeakerStack` hero, the amp-faceplate strip, the
 * 28px-baseline prose block, the downbeat rule, the `DISCOGRAPHY` ledger, the single
 * inverted `PLAY ALL` — is made once, in `Artist.tsx`'s {@link DetailView}. This file
 * is the other half of the fetch-and-adapt split its header describes: it owns
 * `GET /api/tags/:id` and the small number of ways a `TagDetailResponse` differs from
 * an `ArtistDetailResponse`, and nothing about layout.
 *
 * Three differences, all in the data, none in the markup:
 *
 *  1. **The prose is one line, not a bio.** `definition` is a single string (or
 *     `null` — again "no data source yet", the same v1 shape `bio` has); it becomes
 *     {@link DetailView}'s one-element `proseParagraphs` array rather than a
 *     multi-paragraph split, which is exactly the "one-line definition in place of
 *     the biography" the design calls for — no separate rendering path, just a
 *     shorter array. `sources` is always `[]`: `TagDetailResponseSchema` carries no
 *     such field, unlike an artist's.
 *  2. **The faceplate has two cells, not four.** {@link tagFaceplate} (declared in
 *     `Artist.tsx`, shared here) has nothing to put in `РИДДИМОВ` or the duration
 *     cell — `TagDetailResponseSchema` has no `stats` object at all, only the tag's
 *     own `trackCount` / `firstYear` / `lastYear` — so those two cells are not
 *     constructed, and `FaceplateStrip` prints exactly what is real (T45 acceptance
 *     5, again).
 *  3. **A tag's own kind never appears as invented vocabulary.** `TagSummary.kind`
 *     (`riddim | label | style | year | misc`) is real data this file has and does
 *     not print anywhere on the page — `design-direction.md` §8.6 does not ask for
 *     it, and printing an English kind label next to a Cyrillic tag name would be
 *     exactly the kind of invented UI chrome §11 warns against. It stays available
 *     on `TagSummary` for whichever screen actually needs it (the drawer index).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import type { TrackSummary } from '@bl/shared';

import type { RouteName, RouteParams } from '../app/router';
import { ErrorState } from '../app/states/ErrorState';
import { Loading } from '../app/states/Loading';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import type { ApiClient, ApiFailure } from '../lib/api';
import { api, isApiErrorCode, toApiFailure } from '../lib/api';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';

import {
  DetailView,
  TAG_SCREEN_LABEL_RU,
  invalidIdFailure,
  parseRouteId,
  tagFaceplate,
} from './Artist';
import styles from './artist.module.css';

export type TagClient = Pick<ApiClient, 'tag'>;

export interface TagDeps {
  readonly client?: TagClient;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
  readonly telegram?: TelegramFacade;
}

export interface TagProps {
  readonly route?: RouteName;
  readonly params?: RouteParams;
  readonly deps?: TagDeps;
}

interface TracksPage {
  readonly items: readonly TrackSummary[];
  readonly nextCursor: string | null;
}

const EMPTY_TRACKS_PAGE: TracksPage = Object.freeze({ items: [], nextCursor: null });

type LoadStatus = 'loading' | 'ready' | 'error';

interface TagSnapshot {
  readonly name: string;
  readonly trackCount: number;
  readonly firstYear: number | null;
  readonly lastYear: number | null;
  readonly definition: string | null;
}

export function Tag({ params = {}, deps = {} }: TagProps): JSX.Element {
  const client = deps.client ?? api;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;
  const tg = deps.telegram ?? defaultTelegram;

  const tagId = useMemo(() => parseRouteId(params['id']), [params]);

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [error, setError] = useState<ApiFailure | null>(null);
  const [tag, setTag] = useState<TagSnapshot | null>(null);
  const [tracksPage, setTracksPage] = useState<TracksPage>(EMPTY_TRACKS_PAGE);
  const [loadingMore, setLoadingMore] = useState(false);

  const currentTrackId = player((state) => state.trackId);
  const liveIdRef = useRef<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (tagId === null) {
      setStatus('error');
      setError(invalidIdFailure());
      return;
    }

    liveIdRef.current = tagId;
    const controller = new AbortController();
    let live = true;
    setStatus('loading');
    setError(null);

    client
      .tag(tagId, undefined, { signal: controller.signal })
      .then((response) => {
        if (!live) return;
        setTag({
          name: response.tag.name,
          trackCount: response.tag.trackCount,
          firstYear: response.tag.firstYear,
          lastYear: response.tag.lastYear,
          definition: response.definition,
        });
        setTracksPage({ items: response.tracks.items, nextCursor: response.tracks.nextCursor });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (!live || isApiErrorCode(err, 'ABORTED')) return;
        setStatus('error');
        setError(toApiFailure(err));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, tagId, reloadToken]);

  const loadMore = useCallback((): void => {
    const id = liveIdRef.current;
    if (id === null || tracksPage.nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    client
      .tag(id, { cursor: tracksPage.nextCursor })
      .then((response) => {
        setTracksPage((previous) => ({
          items: [...previous.items, ...response.tracks.items],
          nextCursor: response.tracks.nextCursor,
        }));
      })
      .catch(() => {
        /* A failed "load more" leaves the ledger exactly as it was (§8.8) — see
           `Artist.tsx`'s identical comment on its own `loadMore`. */
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [client, loadingMore, tracksPage.nextCursor]);

  const playAll = useCallback((): void => {
    tg.haptics.impact('rigid');
    const w = deps.wiring ?? getWiring();
    void queue
      .getState()
      .replace(tracksPage.items, 0)
      .then(() => w.select(0));
  }, [deps.wiring, queue, tg, tracksPage.items]);

  const playTrack = useCallback(
    (index: number): void => {
      tg.haptics.impact('rigid');
      const w = deps.wiring ?? getWiring();
      void queue
        .getState()
        .replace(tracksPage.items, index)
        .then(() => w.select(index));
    },
    [deps.wiring, queue, tg, tracksPage.items],
  );

  const retry = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  if (status === 'error') {
    return (
      <div className={styles.screen} data-screen="tag">
        <ErrorState
          failure={error ?? invalidIdFailure()}
          grid="ledger"
          onRetry={retry}
          className={styles.error}
        />
      </div>
    );
  }

  if (status === 'loading' || tag === null) {
    return (
      <div className={styles.screen} data-screen="tag">
        <Loading grid="ledger" rows={4} />
      </div>
    );
  }

  /* A one-line definition is a `bio` with exactly one paragraph — `DetailView` does
     not know or care that this string came from a different field than an artist's. */
  const proseParagraphs =
    tag.definition === null || tag.definition.trim() === '' ? [] : [tag.definition.trim()];

  return (
    <DetailView
      dataScreen="tag"
      screenLabel={TAG_SCREEN_LABEL_RU}
      heroName={tag.name}
      faceplate={tagFaceplate(tag.trackCount, tag.firstYear, tag.lastYear)}
      proseParagraphs={proseParagraphs}
      sources={[]}
      tracks={tracksPage.items}
      currentTrackId={currentTrackId}
      hasMore={tracksPage.nextCursor !== null}
      loadingMore={loadingMore}
      onLoadMore={loadMore}
      onPlayAll={playAll}
      onPlayTrack={playTrack}
    />
  );
}
